/**
 * The single place this frontend talks to FastAPI.
 *
 * Every path is relative. In production the static export is served by the
 * same FastAPI process, and in development next.config.mjs rewrites these
 * paths to :8000 -- so the browser is always on one origin and the httpOnly
 * `SameSite=Lax` session cookie is sent automatically. Nothing here handles
 * tokens, because there are none to handle.
 */

import type {
  AdminUser,
  ArchitectChatMessage,
  ArchitectChatResult,
  ArchitectureExplanation,
  CostDelta,
  CostEstimate,
  CurrentUser,
  DiagramResponse,
  DiagramSpec,
  HistoryEntry,
  LoginResult,
  NetworkPlanSummary,
  NetworkExpertPreview,
  PromptEnhanceResult,
  RepoAuditEntry,
  RepoCredential,
  RepoJob,
  RepoLink,
  RepoPlan,
  RepoPullRequest,
  TerraformConfigSchema,
  TerraformCustomization,
  TerraformFilesResult,
  TerraformReview,
  TerraformValidateResult,
  TerramAnalysis,
  TopologyFixItem,
  TopologyValidationResult,
  VersionDiff,
} from './types';

/** Thrown for any non-2xx response, carrying FastAPI's `detail` string. */
export class ApiError extends Error {
  readonly status: number;
  readonly detail: string;

  constructor(status: number, detail: string) {
    super(detail);
    this.name = 'ApiError';
    this.status = status;
    this.detail = detail;
  }

  /** 401 means "no session"; the caller should route to sign-in, not alert. */
  get isUnauthenticated() {
    return this.status === 401;
  }

  /** 402/403 from the credit and plan-limit guards in app/main.py. */
  get isQuotaExceeded() {
    return this.status === 402 || this.status === 429;
  }
}

async function readError(res: Response): Promise<string> {
  try {
    const body = await res.json();
    const detail = (body as { detail?: unknown }).detail;
    if (typeof detail === 'string') return detail;
    if (Array.isArray(detail)) {
      // FastAPI validation errors arrive as a list of {loc, msg, type}.
      return detail
        .map((d: { loc?: unknown[]; msg?: string }) =>
          d.loc ? `${d.loc.slice(1).join('.')}: ${d.msg}` : d.msg
        )
        .filter(Boolean)
        .join('; ');
    }
    if (detail) return JSON.stringify(detail);
  } catch {
    /* fall through to the status text */
  }
  return res.statusText || `Request failed (${res.status})`;
}

interface RequestOptions {
  method?: string;
  body?: unknown;
  signal?: AbortSignal;
}

async function request<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  const { method = 'GET', body, signal } = opts;
  const res = await fetch(path, {
    method,
    signal,
    credentials: 'same-origin',
    headers: body === undefined ? {} : { 'Content-Type': 'application/json' },
    body: body === undefined ? undefined : JSON.stringify(body),
  });

  if (!res.ok) throw new ApiError(res.status, await readError(res));
  if (res.status === 204) return undefined as T;

  const contentType = res.headers.get('content-type') || '';
  if (!contentType.includes('application/json')) return (await res.text()) as T;
  return (await res.json()) as T;
}

/** For endpoints that stream a file back (Terraform ZIP, PNG export). */
async function requestBlob(path: string, opts: RequestOptions = {}): Promise<Blob> {
  const { method = 'POST', body, signal } = opts;
  const res = await fetch(path, {
    method,
    signal,
    credentials: 'same-origin',
    headers: body === undefined ? {} : { 'Content-Type': 'application/json' },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  if (!res.ok) throw new ApiError(res.status, await readError(res));
  return res.blob();
}

const get = <T>(path: string, signal?: AbortSignal) => request<T>(path, { signal });
const post = <T>(path: string, body?: unknown, signal?: AbortSignal) =>
  request<T>(path, { method: 'POST', body, signal });
const del = <T>(path: string, body?: unknown) => request<T>(path, { method: 'DELETE', body });

/**
 * Fills in the DiagramResponse fields a stored history entry does not carry.
 *
 * history_store persists only what it needs to redraw a version -- spec,
 * image_url, legend, cost, insights, node_regions, suggestion_labels. It has
 * no edge_regions, group_regions, or draft state, so an entry loaded from
 * /api/history/{id} is NOT a DiagramResponse even though it looks like one.
 * Handing it to the canvas raw is what made `group_regions.map(...)` throw a
 * client-side exception. Every ingestion path goes through here.
 */
export function normalizeDiagram(
  raw: Partial<DiagramResponse> & { diagram_id: string }
): DiagramResponse {
  return {
    diagram_id: raw.diagram_id,
    image_url: raw.image_url ?? `/diagrams/${raw.diagram_id}.png`,
    spec: raw.spec as DiagramResponse['spec'],
    legend: raw.legend ?? [],
    cost: raw.cost ?? {},
    insights: raw.insights ?? {},
    show_feedback: raw.show_feedback ?? false,
    node_regions: raw.node_regions ?? [],
    edge_regions: raw.edge_regions ?? [],
    group_regions: raw.group_regions ?? [],
    root_diagram_id: raw.root_diagram_id ?? null,
    suggestion_labels: raw.suggestion_labels ?? {},
    /* A history entry predates this field, so its boxes fall back to the
       nesting heuristic rather than losing their colour entirely. */
    boundary_kinds: raw.boundary_kinds ?? {},
    node_lanes: raw.node_lanes ?? {},
    node_ranks: raw.node_ranks ?? {},
    connection_bundles: raw.connection_bundles ?? [],
    /* Same fallback reasoning as boundary_kinds: a stored diagram from before
       the plan was sent lays out the way it always did. */
    layout_plan: raw.layout_plan ?? undefined,
    service_labels: raw.service_labels ?? {},
    /* Same fallback as above, and for the same reason: a stored version from
       before the frame existed lays out the old way rather than losing its
       boundaries. */
    frame: raw.frame ?? [],
    cost_delta: raw.cost_delta ?? null,
    image_source: raw.image_source ?? null,
    repo_image_used: raw.repo_image_used ?? false,
    repo_image_path: raw.repo_image_path ?? null,
    is_draft: raw.is_draft ?? false,
    draft_change_count: raw.draft_change_count ?? 0,
    base_version_number: raw.base_version_number ?? null,
    version_number: raw.version_number ?? null,
    prompt_trace: raw.prompt_trace ?? null,
    saved_mode: raw.saved_mode ?? null,
  };
}

/** Saves a Blob to disk via a transient anchor. Browser-only. */
export function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

/* ── Auth ─────────────────────────────────────────────────────────────── */

export const auth = {
  me: (signal?: AbortSignal) => get<CurrentUser>('/api/me', signal),
  login: (username: string, password: string) =>
    post<LoginResult>('/api/login', { username, password }),
  register: (email: string, password: string, name: string) =>
    post<{ ok: boolean; status?: string; message?: string }>('/api/register', {
      email,
      password,
      name,
    }),
  logout: () => post<{ ok: boolean }>('/api/logout'),
  forgotPassword: (email: string) => post<{ ok: boolean }>('/api/password/forgot', { email }),
  resetPassword: (token: string, password: string) =>
    post<{ ok: boolean }>('/api/password/reset', { token, password }),
};

/* ── Diagram generation and editing ───────────────────────────────────── */

export const diagrams = {
  /** POST /generate -- the main create path. Charges an AI credit. */
  generate: (prompt: string, promptTrace?: Record<string, unknown>, signal?: AbortSignal) =>
    post<DiagramResponse>('/generate', { prompt, prompt_trace: promptTrace ?? null }, signal),

  /** POST /edit -- natural-language edit; produces an uncommitted draft. */
  edit: (
    currentSpec: DiagramSpec,
    instruction: string,
    parentDiagramId?: string | null,
    rootDiagramId?: string | null
  ) =>
    post<DiagramResponse>('/edit', {
      current_spec: currentSpec,
      instruction,
      parent_diagram_id: parentDiagramId ?? null,
      root_diagram_id: rootDiagramId ?? null,
    }),

  /** POST /render-spec -- deterministic re-render of a hand-edited spec. */
  renderSpec: (spec: DiagramSpec, parentDiagramId?: string | null, rootDiagramId?: string | null) =>
    post<DiagramResponse>('/render-spec', {
      spec,
      parent_diagram_id: parentDiagramId ?? null,
      root_diagram_id: rootDiagramId ?? null,
    }),

  standardAlternative: (
    spec: DiagramSpec,
    parentDiagramId?: string | null,
    rootDiagramId?: string | null
  ) =>
    post<DiagramResponse>('/api/standard-alternative', {
      spec,
      parent_diagram_id: parentDiagramId ?? null,
      root_diagram_id: rootDiagramId ?? null,
    }),

  /** Commits the lineage's draft. 'same' writes it back into the version the
   *  draft was based on instead of creating the next one. */
  saveVersion: (rootDiagramId: string, mode: 'new' | 'same' = 'new') =>
    post<DiagramResponse>('/api/save-version', { root_diagram_id: rootDiagramId, mode }),

  discardDraft: (rootDiagramId: string) =>
    post<DiagramResponse>('/api/discard-draft', { root_diagram_id: rootDiagramId }),

  draftStatus: (rootDiagramId: string) =>
    get<{ is_draft: boolean; draft_change_count: number; version_number: number | null }>(
      `/api/draft-status?root_diagram_id=${encodeURIComponent(rootDiagramId)}`
    ),

  /* Deterministic, no-LLM structural edits. */
  replaceNode: (
    spec: DiagramSpec,
    nodeId: string,
    newType: string,
    parentDiagramId?: string | null,
    rootDiagramId?: string | null
  ) =>
    post<DiagramResponse>('/api/node/replace', {
      spec,
      node_id: nodeId,
      new_type: newType,
      parent_diagram_id: parentDiagramId ?? null,
      root_diagram_id: rootDiagramId ?? null,
    }),

  addNode: (body: {
    spec: DiagramSpec;
    new_type: string;
    label?: string | null;
    group?: string | null;
    connect_from?: string | null;
    edge_label?: string | null;
    parent_diagram_id?: string | null;
    root_diagram_id?: string | null;
  }) => post<DiagramResponse>('/api/node/add', body),

  addEdge: (body: {
    spec: DiagramSpec;
    source: string;
    target: string;
    label?: string | null;
    parent_diagram_id?: string | null;
    root_diagram_id?: string | null;
  }) => post<DiagramResponse>('/api/edge/add', body),

  /** LLM edit scoped to one node ("AI edit this node"). Charges a credit. */
  askAiForNode: (body: {
    spec: DiagramSpec;
    node_id: string;
    instruction: string;
    parent_diagram_id?: string | null;
    root_diagram_id?: string | null;
  }) => post<DiagramResponse>('/api/node/ask-ai', body),

  /**
   * Deterministic boundary-box edit. `set_node_group` with a null
   * `new_group_id` is what "Remove from boundary" uses -- it moves the named
   * services out of every box rather than deleting the box.
   */
  editGroup: (body: {
    spec: DiagramSpec;
    operation: 'remove' | 'rename' | 'hide_all' | 'set_node_group' | string;
    group_id?: string | null;
    label?: string | null;
    node_ids?: string[];
    new_group_id?: string | null;
    parent_diagram_id?: string | null;
    root_diagram_id?: string | null;
  }) => post<DiagramResponse>('/api/group/edit', body),

  /**
   * The network layer. Separate from editGroup because these boundaries are
   * infrastructure: they carry an address range, they have a legal parent, and
   * the generated Terraform reads both. The server allocates and validates the
   * range -- a 400 here is a shape that cannot deploy, and its detail is a
   * sentence written to be shown to the user as-is.
   */
  networkPlan: (spec: DiagramSpec, nodeIds: string[] = [], signal?: AbortSignal) =>
    post<NetworkPlanSummary>('/api/network/plan', { spec, node_ids: nodeIds }, signal),

  networkExpertPreview: (
    spec: DiagramSpec,
    options: Record<string, unknown> = {},
    signal?: AbortSignal
  ) => post<NetworkExpertPreview>('/api/network-expert/preview', { spec, options }, signal),

  networkExpertApply: (
    spec: DiagramSpec,
    acceptedChangeIds: string[],
    options: Record<string, unknown> = {},
    parentDiagramId?: string | null,
    rootDiagramId?: string | null
  ) =>
    post<DiagramResponse>('/api/network-expert/apply', {
      spec,
      accepted_change_ids: acceptedChangeIds,
      options,
      parent_diagram_id: parentDiagramId ?? null,
      root_diagram_id: rootDiagramId ?? null,
    }),

  createBoundary: (body: {
    spec: DiagramSpec;
    kind: 'network' | 'subnet' | 'scope' | 'logical';
    label?: string | null;
    node_ids?: string[];
    parent_group_id?: string | null;
    cidr?: string | null;
    parent_diagram_id?: string | null;
    root_diagram_id?: string | null;
  }) => post<DiagramResponse>('/api/network/boundary', body),

  addPrivateEndpoint: (body: {
    spec: DiagramSpec;
    target_node_id: string;
    subnet_group_id?: string | null;
    parent_diagram_id?: string | null;
    root_diagram_id?: string | null;
  }) => post<DiagramResponse>('/api/network/private-endpoint', body),

  /** Label size / edge label size / line width, re-rendered server-side. */
  renderStyle: (body: {
    spec: DiagramSpec;
    font_scale?: number | null;
    edge_label_scale?: number | null;
    edge_width?: number | null;
    parent_diagram_id?: string | null;
    root_diagram_id?: string | null;
  }) => post<DiagramResponse>('/api/render-style', body),
};

/* ── Prompt assists ───────────────────────────────────────────────────── */

export const prompts = {
  enhance: (prompt: string) => post<PromptEnhanceResult>('/api/prompt/enhance', { prompt }),
  architectChat: (messages: ArchitectChatMessage[], signal?: AbortSignal) =>
    post<ArchitectChatResult>('/api/architect-chat', { messages }, signal),
};

/* ── Lazy analysis panels ─────────────────────────────────────────────── */

export const analysis = {
  /**
   * Cost is deliberately lazy and off the render hot path. Passing
   * `assumptions` makes it a what-if recalculation that is NOT persisted
   * over the diagram's own estimate.
   */
  cost: (
    diagramId: string,
    spec: DiagramSpec,
    rootDiagramId?: string | null,
    assumptions?: Record<string, unknown> | null
  ) =>
    post<{ cost: CostEstimate; cost_delta: CostDelta | null; recalculated?: boolean }>(
      '/api/cost',
      {
        diagram_id: diagramId,
        spec,
        root_diagram_id: rootDiagramId ?? null,
        assumptions: assumptions ?? null,
      }
    ),

  /** Returns { explanation }, not a bare report -- same wrapper as
   *  /api/validate-topology. */
  explain: (diagramId: string, spec: DiagramSpec, rootDiagramId?: string | null) =>
    post<{ explanation: ArchitectureExplanation }>('/api/explain', {
      diagram_id: diagramId,
      spec,
      root_diagram_id: rootDiagramId ?? null,
    }),

  /** Returns { structural, intent } -- see TopologyValidationResult. */
  validateTopology: (diagramId: string, spec: DiagramSpec, rootDiagramId?: string | null) =>
    post<TopologyValidationResult>('/api/validate-topology', {
      diagram_id: diagramId,
      spec,
      root_diagram_id: rootDiagramId ?? null,
    }),

  /**
   * Apply the deterministic remedies for structural findings, then re-render.
   *
   * `spec` must be the spec the findings were computed against, and `fixes` the
   * FULL set to apply to it -- never a delta. Undo is then "send the same list
   * minus that entry", which replays the survivors from a clean base instead of
   * trying to unpick one edit from underneath the ones layered on top of it.
   * An empty list is a legitimate "revert every fix" request.
   */
  applyTopologyFixes: (
    spec: DiagramSpec,
    fixes: TopologyFixItem[],
    parentDiagramId?: string | null,
    rootDiagramId?: string | null,
    label?: string
  ) =>
    post<DiagramResponse>('/api/topology/fix', {
      spec,
      fixes,
      label: label ?? null,
      parent_diagram_id: parentDiagramId ?? null,
      root_diagram_id: rootDiagramId ?? null,
    }),
};

/* ── Terraform ────────────────────────────────────────────────────────── */

export const terraform = {
  /** Note: this endpoint takes the spec as the whole body, not `{spec}`. */
  configSchema: (spec: DiagramSpec) =>
    post<TerraformConfigSchema>('/api/terraform/config-schema', spec),

  /** Generates the file set for the editor (no ZIP). */
  files: (spec: DiagramSpec, customization?: TerraformCustomization | null, diagramId?: string) =>
    post<TerraformFilesResult>('/api/terraform/files', {
      spec,
      customization: customization ?? null,
      diagram_id: diagramId ?? null,
    }),

  validate: (files: Record<string, string>, provider: string, autofix = false) =>
    post<TerraformValidateResult>('/api/terraform/validate', { files, provider, autofix }),

  /** Re-runs the architecture review over an edited file set. Free and
   *  offline -- unlike validate, it never shells out to the terraform CLI. */
  lint: (spec: DiagramSpec, files: Record<string, string>) =>
    post<TerraformReview>('/api/terraform/lint', { spec, files }),

  /** Natural-language edit of the open file set. Charges a credit. */
  assist: (
    files: Record<string, string>,
    provider: string,
    instruction: string,
    activeFile?: string | null
  ) =>
    post<{ files: Record<string, string>; changed_files: string[] }>('/api/terraform/assist', {
      files,
      provider,
      instruction,
      active_file: activeFile ?? null,
    }),

  /** The one-click scaffold download (unedited). */
  downloadDefault: (
    spec: DiagramSpec,
    customization?: TerraformCustomization | null,
    diagramId?: string
  ) =>
    requestBlob('/terraform', {
      body: { spec, customization: customization ?? null, diagram_id: diagramId ?? null },
    }),

  /** Zips the edited file set from the editor. */
  downloadFiles: (spec: DiagramSpec, files: Record<string, string>) =>
    requestBlob('/api/terraform/download', { body: { spec, files } }),
};

/* ── History and drafts ───────────────────────────────────────────────── */

export const history = {
  list: (signal?: AbortSignal) => get<{ items: HistoryEntry[] }>('/api/history', signal),
  drafts: () => get<{ items: HistoryEntry[] }>('/api/drafts'),
  /**
   * Returns a stored entry normalized into a full DiagramResponse -- the raw
   * entry is missing several fields the canvas dereferences. See
   * normalizeDiagram.
   */
  entry: async (diagramId: string): Promise<DiagramResponse> =>
    normalizeDiagram(
      await get<Partial<DiagramResponse> & { diagram_id: string }>(`/api/history/${diagramId}`)
    ),
  /** Diffs a version against another; omitting `previousDiagramId` picks the
   *  preceding version in the same lineage. */
  compare: (diagramId: string, previousDiagramId?: string | null) =>
    post<VersionDiff>('/api/history/compare', {
      diagram_id: diagramId,
      previous_diagram_id: previousDiagramId ?? null,
    }),
  remove: (diagramId: string) => del<{ ok: boolean }>(`/api/history/${diagramId}`),
};

/* ── TerraM (import existing Terraform) ───────────────────────────────── */

export const terram = {
  /**
   * Multipart upload of a .zip of an existing Terraform project. Returns a
   * review payload, not a diagram -- the user approves it first, then calls
   * generate() with the same session_id. Charges a credit.
   */
  analyze: async (file: File) => {
    const form = new FormData();
    form.append('file', file);
    // No Content-Type header: the browser must set the multipart boundary.
    const res = await fetch('/api/terram/analyze', {
      method: 'POST',
      credentials: 'same-origin',
      body: form,
    });
    if (!res.ok) throw new ApiError(res.status, await readError(res));
    return (await res.json()) as TerramAnalysis;
  },

  generate: (sessionId: string, prompt: string, acceptedIssueIds: string[]) =>
    post<DiagramResponse>('/api/terram/generate', {
      session_id: sessionId,
      prompt,
      accepted_issue_ids: acceptedIssueIds,
    }),
  baselineStatus: (rootDiagramId: string) =>
    get<Record<string, unknown>>(
      `/api/terram/baseline-status?root_diagram_id=${encodeURIComponent(rootDiagramId)}`
    ),
};

/* ── Repositories ─────────────────────────────────────────────────────── */

/* ── Repositories (app/repos) ─────────────────────────────────────────── */

/**
 * The real repo module lives under /api/repos, not the older
 * /api/repo-links/* routes in main.py -- that is a separate, thinner path this
 * client was pointed at by mistake. This one is what the DevOps workspace
 * needs: capabilities probing, sync planning, jobs, pull requests, and audit.
 */
export const repos = {
  info: () => get<Record<string, unknown>>('/api/repos/info'),

  /* Credentials are scoped to this module and support rotation/validation,
     which the generic credential store does not. */
  listCredentials: () => get<{ items: RepoCredential[] }>('/api/repos/credentials'),
  createCredential: (body: {
    provider: string;
    label: string;
    token?: string;
    organization?: string;
  }) => post<RepoCredential>('/api/repos/credentials', body),
  validateCredential: (id: string) =>
    post<Record<string, unknown>>(`/api/repos/credentials/${id}/validate`),
  rotateCredential: (id: string, body: { token: string }) =>
    post<Record<string, unknown>>(`/api/repos/credentials/${id}/rotate`, body),
  removeCredential: (id: string) => del<{ ok: boolean }>(`/api/repos/credentials/${id}`),

  discover: (body: { provider: string; credential_id: string; owner?: string; project?: string }) =>
    post<{
      items: {
        provider: string;
        owner: string;
        project: string | null;
        repo: string;
        repository_id?: string | null;
        default_branch?: string;
        private?: boolean;
        url?: string;
        can_push?: boolean;
        archived?: boolean;
      }[];
    }>('/api/repos/repositories', body),
  /** What this credential may actually do on that repo (push, PRs, workflows). */
  capabilities: (body: Record<string, unknown>) =>
    post<Record<string, unknown>>('/api/repos/capabilities', body),

  list: (rootDiagramId?: string | null) =>
    get<{ items: RepoLink[] }>(
      rootDiagramId
        ? `/api/repos/links?root_diagram_id=${encodeURIComponent(rootDiagramId)}`
        : '/api/repos/links'
    ),
  create: (body: Record<string, unknown>) => post<RepoLink>('/api/repos/links', body),
  get: (linkId: string) => get<RepoLink>(`/api/repos/links/${linkId}`),
  patch: (linkId: string, body: Record<string, unknown>) =>
    request<RepoLink>(`/api/repos/links/${linkId}`, { method: 'PATCH', body }),
  remove: (linkId: string) => del<{ ok: boolean }>(`/api/repos/links/${linkId}`),
  refresh: (linkId: string) => post<RepoLink>(`/api/repos/links/${linkId}/refresh`),

  /** Dry run: what a push would write, before it writes it. */
  plan: (linkId: string, body: Record<string, unknown>) =>
    post<RepoPlan>(`/api/repos/links/${linkId}/plan`, body),
  push: (linkId: string, body: Record<string, unknown>) =>
    post<RepoJob>(`/api/repos/links/${linkId}/push`, body),
  pull: (linkId: string, body: Record<string, unknown> = {}) =>
    post<RepoJob>(`/api/repos/links/${linkId}/pull`, body),
  resolve: (linkId: string, body: Record<string, unknown>) =>
    post<Record<string, unknown>>(`/api/repos/links/${linkId}/resolve`, body),

  branches: (linkId: string) => get<{ items: string[] }>(`/api/repos/links/${linkId}/branches`),
  branchState: (linkId: string) =>
    get<Record<string, unknown>>(`/api/repos/links/${linkId}/branch-state`),
  createBranch: (linkId: string, body: { name: string; base?: string }) =>
    post<Record<string, unknown>>(`/api/repos/links/${linkId}/branches`, body),
  switchBranch: (linkId: string, body: { branch: string }) =>
    post<Record<string, unknown>>(`/api/repos/links/${linkId}/switch-branch`, body),
  deleteBranch: (linkId: string, branch: string) =>
    del<{ ok: boolean }>(`/api/repos/links/${linkId}/branches/${encodeURIComponent(branch)}`),
  compare: (linkId: string) => get<Record<string, unknown>>(`/api/repos/links/${linkId}/compare`),

  openPullRequest: (linkId: string, body: Record<string, unknown>) =>
    post<RepoPullRequest>(`/api/repos/links/${linkId}/pull-requests`, body),
  pullRequest: (linkId: string, number: number) =>
    get<RepoPullRequest>(`/api/repos/links/${linkId}/pull-requests/${number}`),
  pullRequestChecks: (linkId: string, number: number) =>
    get<Record<string, unknown>>(`/api/repos/links/${linkId}/pull-requests/${number}/checks`),
  pullRequestReadiness: (linkId: string, number: number) =>
    get<Record<string, unknown>>(`/api/repos/links/${linkId}/pull-requests/${number}/readiness`),
  mergePullRequest: (linkId: string, number: number, body: Record<string, unknown> = {}) =>
    post<Record<string, unknown>>(`/api/repos/links/${linkId}/pull-requests/${number}/merge`, body),

  /* Push and pull are asynchronous jobs -- the workspace polls these. */
  job: (jobId: string) => get<RepoJob>(`/api/repos/jobs/${jobId}`),
  jobs: () => get<{ items: RepoJob[] }>('/api/repos/jobs'),
  cancelJob: (jobId: string) => post<RepoJob>(`/api/repos/jobs/${jobId}/cancel`),

  audit: () =>
    get<{ items: RepoAuditEntry[]; integrity?: Record<string, unknown> }>('/api/repos/audit'),
};

/* ── Credentials and deployments (feature-flagged server-side) ────────── */

export const deployments = {
  listCredentials: () =>
    get<{ items: { id: string; provider: string; label: string }[] }>('/api/credentials'),
  saveCredential: (provider: string, label: string, fields: Record<string, string>) =>
    post<{ id: string }>('/api/credentials', { provider, label, fields }),
  removeCredential: (credentialId: string) =>
    del<{ ok: boolean }>(`/api/credentials/${credentialId}`),

  list: () => get<{ items: Record<string, unknown>[] }>('/api/deployments'),
  create: (body: Record<string, unknown>) =>
    post<Record<string, unknown>>('/api/deployments', body),
  detail: (id: string) => get<Record<string, unknown>>(`/api/deployments/${id}`),
  remove: (id: string) => del<{ ok: boolean }>(`/api/deployments/${id}`),
  plan: (id: string, body: Record<string, unknown>) =>
    post<Record<string, unknown>>(`/api/deployments/${id}/plan`, body),
  apply: (id: string, body: Record<string, unknown> = {}) =>
    post<Record<string, unknown>>(`/api/deployments/${id}/apply`, body),
  drift: (id: string, body: Record<string, unknown> = {}) =>
    post<Record<string, unknown>>(`/api/deployments/${id}/drift`, body),
  destroy: (id: string, confirmLabel: string) =>
    post<Record<string, unknown>>(`/api/deployments/${id}/destroy`, {
      confirm_label: confirmLabel,
    }),
  /** Raw `terraform plan` / `terraform plan -refresh-only` output, as text. */
  rawPlan: (id: string) => get<string>(`/api/deployments/${id}/raw-plan`),
  rawDrift: (id: string) => get<string>(`/api/deployments/${id}/raw-drift`),
  lockStatus: (id: string) => get<Record<string, unknown>>(`/api/deployments/${id}/lock-status`),
  forceUnlock: (id: string, lockId: string) =>
    post<Record<string, unknown>>(`/api/deployments/${id}/force-unlock`, { lock_id: lockId }),
};

/* ── Admin ────────────────────────────────────────────────────────────── */

export const admin = {
  users: (signal?: AbortSignal) =>
    get<{ items: AdminUser[]; registration_open: boolean }>('/api/admin/users', signal),
  approve: (username: string, creditInr?: number) =>
    post<{ ok: boolean }>(
      `/api/admin/users/${encodeURIComponent(username)}/approve`,
      creditInr === undefined ? undefined : { credit_inr: creditInr }
    ),
  reject: (username: string) =>
    post<{ ok: boolean }>(`/api/admin/users/${encodeURIComponent(username)}/reject`),
  suspend: (username: string) =>
    post<{ ok: boolean }>(`/api/admin/users/${encodeURIComponent(username)}/suspend`),
  reactivate: (username: string) =>
    post<{ ok: boolean }>(`/api/admin/users/${encodeURIComponent(username)}/reactivate`),
  remove: (username: string) =>
    del<{ ok: boolean }>(`/api/admin/users/${encodeURIComponent(username)}`),
  setLimit: (username: string, limit: number) =>
    post<{ ok: boolean }>(`/api/admin/users/${encodeURIComponent(username)}/limit`, { limit }),
  setCredit: (username: string, creditInr: number) =>
    post<{ ok: boolean }>(`/api/admin/users/${encodeURIComponent(username)}/credit`, {
      credit_inr: creditInr,
    }),
  setRegistrationOpen: (open: boolean) => post<{ ok: boolean }>('/api/admin/settings', { open }),
};

/* ── Misc ─────────────────────────────────────────────────────────────── */

export const misc = {
  /** Ranked taxonomy lookup -- returns provider-specific service names. */
  searchTaxonomy: (query: string, provider: string, signal?: AbortSignal) =>
    get<{ results: { key: string; label: string; description: string }[] }>(
      `/api/taxonomy/search?q=${encodeURIComponent(query)}&provider=${encodeURIComponent(provider)}`,
      signal
    ),
  iconUrl: (key: string, provider: string) =>
    `/api/taxonomy/icon?key=${encodeURIComponent(key)}&provider=${encodeURIComponent(provider)}`,
  /**
   * Concepts that are drawn but never provisioned -- users, the internet, an
   * on-prem datacenter. Served by the backend rather than listed here so the
   * Deploy panel and the Terraform generator cannot disagree.
   */
  nonDeployableConcepts: (signal?: AbortSignal) =>
    get<{ concepts: string[] }>('/api/taxonomy/non-deployable', signal),
  submitFeedback: (ratings: Record<string, number>, suggestion?: string) =>
    post<{ ok: boolean }>('/api/feedback/submit', { ratings, suggestion: suggestion ?? null }),
};

export const api = {
  auth,
  diagrams,
  prompts,
  analysis,
  terraform,
  history,
  terram,
  repos,
  deployments,
  admin,
  misc,
};

export default api;
